import { useCallback, useMemo, useState } from "react";
import * as Bananas from "bananas-commerce-admin";
import { useQuery } from "@tanstack/react-query";

import { Autocomplete, Grid2, Stack, TextField } from "@mui/material";

import { useSnackbar } from "notistack";

import { Case, Cause } from "@/extensions/klarahill/types";

import * as Format from "@/utils/format";

export interface CausesCardProps extends Bananas.CardProps {
  case: Case;
  setCase: React.Dispatch<React.SetStateAction<Case>>;
}

export interface CausesCardFromValues {
  causes: string[];
}

const defaultOption = { label: "Välj Standardmottagare", id: "default" };

export const CausesCard: React.FC<CausesCardProps> = ({
  case: c,
  setCase,
  ...props
}) => {
  const api = Bananas.useApi();
  const { enqueueSnackbar } = useSnackbar();

  const { data: pennybridgeCauses } = useQuery({
    queryKey: ["pennybridge", "cause", "list"],
    queryFn: async () => {
      const action = api.operations["pennybridge.cause:list"];
      if (!action) throw new Error('Invalid action "pennybridge.cause:list".');

      const response = await action.call();

      if (response.ok) {
        return (await response.json()) as Cause[];
      } else {
        enqueueSnackbar("Kunde inte hämta gåvoorganisationer.", {
          variant: "error",
        });
        return [];
      }
    },
  });

  const [selectedCauses, setSelectedCauses] = useState<Bananas.FormOption[]>([
    ...(c.causes?.map(({ name, id }) => ({ label: name, id })) ?? []),
  ]);

  const options = useMemo(() => {
    const publishedCauses =
      pennybridgeCauses?.filter(({ is_published }) => is_published) ?? [];

    const hasSelectedAllPublishedCauses = publishedCauses.every((pc) =>
      selectedCauses.some((sc) => sc.id === pc.id),
    );

    return [
      ...(hasSelectedAllPublishedCauses ? [] : [defaultOption]),
      ...(pennybridgeCauses?.map(({ name, id }) => ({ label: name, id })) ??
        []),
    ];
  }, [pennybridgeCauses, selectedCauses]);

  const handleCauseChange = useCallback(
    (causes: Bananas.FormOption[]) => {
      if (causes.some((c) => c.id === "default")) {
        const newCauses = causes.filter((c) => c.id !== "default");
        pennybridgeCauses?.forEach((cause) => {
          if (cause.is_published && !newCauses.some((c) => c.id === cause.id)) {
            newCauses.push({ label: cause.name, id: cause.id });
          }
        });
        setSelectedCauses(newCauses);
      } else {
        setSelectedCauses(causes);
      }
    },
    [pennybridgeCauses],
  );

  return (
    <Bananas.Card<CausesCardFromValues>
      isCompact
      isEditable
      onSubmit={async (_, event) => {
        const form = event.currentTarget;
        const formData = new FormData(form);
        const causes = formData.getAll("causes");
        if (causes == null || causes.length === 0)
          return "Ingen förändring av mottagre.";

        const action = api.operations["case.case:update"];
        if (!action) throw new Error('Invalid action "case.case:update".');

        const response = await action.call({
          params: { case_id: c.id },
          body: { cause_ids: causes }, // TODO: Check that we get the right thing (the id)
        });

        if (response.ok) {
          const data: Case = await response.json();
          setCase(data);
          return "Minnesgåvomottagare ändrade.";
        } else {
          throw response;
        }
      }}
      {...props}
    >
      <Bananas.CardHeader title="Minnesgåva" />

      <Bananas.CardContent>
        <Bananas.CardRow>
          <CustomCardFieldAutoComplete
            fallback="Standardmottagare"
            formName="causes"
            label="Mottagare"
            options={options}
            value={selectedCauses}
            onChange={handleCauseChange}
          />
        </Bananas.CardRow>
        <Bananas.CardRow>
          <Bananas.CardFieldText
            formName="totalGiftAmount"
            isEditable={false}
            label="Totalt gåvobelopp exkl. avgift"
            value={Format.price(c.gift_total ?? 0)}
          />
        </Bananas.CardRow>
      </Bananas.CardContent>

      <Bananas.CardActions>
        <Bananas.CardCancelButton />
        <Bananas.CardSaveButton />
      </Bananas.CardActions>
    </Bananas.Card>
  );
};

interface CustomCardFieldAutoCompleteProps
  extends Omit<Bananas.CardFieldAutoCompleteProps, "type"> {
  onChange: (value: Bananas.FormOption[]) => void;
}

const CustomCardFieldAutoComplete: React.FC<
  CustomCardFieldAutoCompleteProps
> = ({
  fallback = "—",
  fallbackPredicate = (value) =>
    value == null || (Array.isArray(value) && value.length === 0),
  formName,
  helperText,
  isEditable = true,
  isReadable = true,
  label,
  options,
  size = "grow",
  value: selectedOptions,
  onChange: setSelectedOptions,
  ...props
}) => {
  const { isCompact, isEditing } = Bananas.useCardContext();

  return (
    <Grid2 size={size}>
      {isEditing && isEditable ? (
        <Stack alignItems="center" justifyContent="space-between">
          <Autocomplete
            isOptionEqualToValue={(
              option: Bananas.FormOption,
              value: Bananas.FormOption,
            ) => option.id === value.id}
            multiple={true}
            options={options ?? []}
            renderInput={(props) => <TextField {...props} label={label} />}
            sx={{ width: "100%" }}
            value={selectedOptions}
            onChange={(_, value) =>
              setSelectedOptions(value as Bananas.FormOption[])
            }
            {...props}
          />

          {/* Render hidden inputs for form submission */}
          {selectedOptions.map((option) => (
            <input
              key={option.id}
              name={formName}
              type="hidden"
              value={option.id}
            />
          ))}
        </Stack>
      ) : (
        isReadable && (
          <Bananas.LabeledValue
            compact={isCompact}
            label={label}
            value={
              fallbackPredicate(selectedOptions)
                ? fallback
                : selectedOptions.map(({ label }) => label).join(", ")
            }
          />
        )
      )}
    </Grid2>
  );
};
